"use client"

import { useState, useEffect } from "react"
import { collection, getDocs, deleteDoc, doc, setDoc, writeBatch } from "firebase/firestore"
import { db } from "../firebase"
import {
  FaUsers,
  FaPlus,
  FaTrash,
  FaEdit,
  FaImage,
  FaUser,
  FaBriefcase,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaUndo,
  FaSave,
  FaGripVertical,
  FaArrowUp,
  FaArrowDown,
  FaSortAmountUp,
} from "react-icons/fa"

const CommitteeUpdateForm = () => {
  const [members, setMembers] = useState([])
  const [selectedCommittee, setSelectedCommittee] = useState("")
  const [form, setForm] = useState({ name: "", designation: "", photoUrl: "" })
  const [editingMemberId, setEditingMemberId] = useState(null)
  const [saveStatus, setSaveStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [visibleSections, setVisibleSections] = useState(new Set())
  const [draggedItem, setDraggedItem] = useState(null)
  const [dragOverItem, setDragOverItem] = useState(null)
  const [isReordering, setIsReordering] = useState(false)

  const committees = [
    { id: "Alumni and Industry Connect", color: "from-blue-500 to-blue-700" },
    { id: "Editorial", color: "from-green-500 to-green-700" },
    { id: "Graphics and Design", color: "from-purple-500 to-purple-700" },
    { id: "Program", color: "from-orange-500 to-orange-700" },
    { id: "Publicity", color: "from-pink-500 to-pink-700" },
    { id: "Public Relations", color: "from-indigo-500 to-indigo-700" },
    { id: "Social and Environmental", color: "from-teal-500 to-teal-700" },
    { id: "Technical", color: "from-red-500 to-red-700" },
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const fetchMembers = async (committeeName) => {
    if (!committeeName) return
    try {
      setLoading(true)
      const snapshot = await getDocs(collection(db, `committees/${committeeName}/members`))
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

      // Sort by order field, then by designation priority (Head first, Co-Head second)
      const sortedData = data.sort((a, b) => {
        // First sort by order if it exists
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order
        }
        if (a.order !== undefined) return -1
        if (b.order !== undefined) return 1

        // Then sort by designation priority
        const designationPriority = { Head: 0, "Co-Head": 1 }
        const aPriority = designationPriority[a.designation] ?? 2
        const bPriority = designationPriority[b.designation] ?? 2

        if (aPriority !== bPriority) return aPriority - bPriority

        // Finally sort by name
        return a.name.localeCompare(b.name)
      })

      setMembers(sortedData)
    } catch (err) {
      console.error("Failed to fetch members:", err)
      setSaveStatus("fetch-error")
      setTimeout(() => setSaveStatus(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedCommittee) fetchMembers(selectedCommittee)
  }, [selectedCommittee])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedCommittee || !form.name || !form.designation || !form.photoUrl) {
      setSaveStatus("validation-error")
      setTimeout(() => setSaveStatus(null), 3000)
      return
    }

    const memberId = editingMemberId || form.name.toLowerCase().replace(/\s+/g, "_")
    const memberRef = doc(db, `committees/${selectedCommittee}/members`, memberId)

    // Assign order based on current members length if it's a new member
    const memberData = {
      ...form,
      order: editingMemberId
        ? (members.find((m) => m.id === editingMemberId)?.order ?? members.length)
        : members.length,
    }

    try {
      setLoading(true)
      await setDoc(memberRef, memberData)
      setForm({ name: "", designation: "", photoUrl: "" })
      setEditingMemberId(null)
      fetchMembers(selectedCommittee)
      setSaveStatus("success")
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (err) {
      console.error("Failed to save member:", err)
      setSaveStatus("error")
      setTimeout(() => setSaveStatus(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return

    try {
      setLoading(true)
      await deleteDoc(doc(db, `committees/${selectedCommittee}/members`, id))
      fetchMembers(selectedCommittee)
      setSaveStatus("deleted")
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (err) {
      console.error("Failed to delete member:", err)
      setSaveStatus("delete-error")
      setTimeout(() => setSaveStatus(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (member) => {
    setForm({
      name: member.name,
      designation: member.designation,
      photoUrl: member.photoUrl,
    })
    setEditingMemberId(member.id)
  }

  const resetForm = () => {
    setForm({ name: "", designation: "", photoUrl: "" })
    setEditingMemberId(null)
  }

  // Drag and Drop Functions
  const handleDragStart = (e, index) => {
    setDraggedItem(index)
    e.dataTransfer.effectAllowed = "move"
    e.target.style.opacity = "0.5"
  }

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1"
    setDraggedItem(null)
    setDragOverItem(null)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
    setDragOverItem(index)
  }

  const handleDragLeave = () => {
    setDragOverItem(null)
  }

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault()

    if (draggedItem === null || draggedItem === dropIndex) return

    const newMembers = [...members]
    const draggedMember = newMembers[draggedItem]

    // Remove dragged item
    newMembers.splice(draggedItem, 1)

    // Insert at new position
    const insertIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex
    newMembers.splice(insertIndex, 0, draggedMember)

    // Update order for all members
    const updatedMembers = newMembers.map((member, index) => ({
      ...member,
      order: index,
    }))

    setMembers(updatedMembers)
    await updateMemberOrder(updatedMembers)
  }

  const updateMemberOrder = async (updatedMembers) => {
    try {
      setIsReordering(true)
      const batch = writeBatch(db)

      updatedMembers.forEach((member) => {
        const memberRef = doc(db, `committees/${selectedCommittee}/members`, member.id)
        batch.update(memberRef, { order: member.order })
      })

      await batch.commit()
      setSaveStatus("reordered")
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (err) {
      console.error("Failed to update order:", err)
      setSaveStatus("reorder-error")
      setTimeout(() => setSaveStatus(null), 3000)
      // Revert on error
      fetchMembers(selectedCommittee)
    } finally {
      setIsReordering(false)
    }
  }

  const moveMember = async (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= members.length) return

    const newMembers = [...members]
    const temp = newMembers[index]
    newMembers[index] = newMembers[newIndex]
    newMembers[newIndex] = temp

    // Update order
    const updatedMembers = newMembers.map((member, idx) => ({
      ...member,
      order: idx,
    }))

    setMembers(updatedMembers)
    await updateMemberOrder(updatedMembers)
  }

  const autoSortMembers = async () => {
    const sortedMembers = [...members].sort((a, b) => {
      const designationPriority = { Head: 0, "Co-Head": 1 }
      const aPriority = designationPriority[a.designation] ?? 2
      const bPriority = designationPriority[b.designation] ?? 2

      if (aPriority !== bPriority) return aPriority - bPriority
      return a.name.localeCompare(b.name)
    })

    const updatedMembers = sortedMembers.map((member, index) => ({
      ...member,
      order: index,
    }))

    setMembers(updatedMembers)
    await updateMemberOrder(updatedMembers)
  }

  const selectedCommitteeData = committees.find((c) => c.id === selectedCommittee)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 dark:bg-blue-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 dark:bg-purple-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div
          data-animate
          id="header"
          className={`text-center mb-16 transform transition-all duration-700 ${
            visibleSections.has("header") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mb-6 shadow-lg">
            <FaUsers className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Committee Management
          </h1>
          <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Manage committee members across different departments with drag-and-drop reordering
          </p>
        </div>

        {/* Status Messages */}
        {saveStatus && (
          <div
            data-animate
            id="status"
            className={`max-w-4xl mx-auto mb-8 transform transition-all duration-700 ${
              visibleSections.has("status") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            {saveStatus === "success" && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center space-x-3">
                <FaCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                <p className="text-green-800 dark:text-green-200 font-medium">Member saved successfully!</p>
              </div>
            )}
            {saveStatus === "deleted" && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-center space-x-3">
                <FaCheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <p className="text-blue-800 dark:text-blue-200 font-medium">Member deleted successfully!</p>
              </div>
            )}
            {saveStatus === "reordered" && (
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 flex items-center space-x-3">
                <FaCheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <p className="text-purple-800 dark:text-purple-200 font-medium">Member order updated successfully!</p>
              </div>
            )}
            {(saveStatus === "error" ||
              saveStatus === "delete-error" ||
              saveStatus === "fetch-error" ||
              saveStatus === "reorder-error") && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center space-x-3">
                <FaExclamationTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <p className="text-red-800 dark:text-red-200 font-medium">
                  {saveStatus === "fetch-error"
                    ? "Failed to load members"
                    : saveStatus === "delete-error"
                      ? "Failed to delete member"
                      : saveStatus === "reorder-error"
                        ? "Failed to update member order"
                        : "Failed to save member"}
                </p>
              </div>
            )}
            {saveStatus === "validation-error" && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-center space-x-3">
                <FaExclamationTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">Please fill in all required fields</p>
              </div>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div
              data-animate
              id="form"
              className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-700 ${
                visibleSections.has("form") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
            >
              <div
                className={`h-2 bg-gradient-to-r ${selectedCommitteeData?.color || "from-blue-500 to-purple-500"}`}
              ></div>

              <div className="p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div
                    className={`w-16 h-16 bg-gradient-to-r ${
                      selectedCommitteeData?.color || "from-blue-500 to-purple-500"
                    } rounded-2xl flex items-center justify-center shadow-lg`}
                  >
                    {editingMemberId ? (
                      <FaEdit className="w-8 h-8 text-white" />
                    ) : (
                      <FaPlus className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                      {editingMemberId ? "Edit Member" : "Add Member"}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {editingMemberId ? "Update member information" : "Add a new committee member"}
                    </p>
                  </div>
                </div>

                {/* Committee Selection */}
                <div className="mb-8">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                    Select Committee <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedCommittee}
                    onChange={(e) => {
                      setSelectedCommittee(e.target.value)
                      setForm({ name: "", designation: "", photoUrl: "" })
                      setEditingMemberId(null)
                    }}
                    className="w-full p-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white font-medium"
                    required
                  >
                    <option value="">Choose a committee...</option>
                    {committees.map((committee) => (
                      <option key={committee.id} value={committee.id}>
                        {committee.id}
                      </option>
                    ))}
                  </select>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaUser className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Enter full name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full pl-12 pr-4 py-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                  </div>

                  {/* Designation Field */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                      Designation <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaBriefcase className="h-5 w-5 text-gray-400" />
                      </div>
                      <select
                        value={form.designation}
                        onChange={(e) => setForm({ ...form, designation: e.target.value })}
                        className="w-full pl-12 pr-4 py-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white appearance-none"
                        required
                      >
                        <option value="">Select designation...</option>
                        <option value="Head">Head</option>
                        <option value="Co-Head">Co-Head</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Photo URL Field */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                      Photo URL <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaImage className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        placeholder="https://example.com/photo.jpg"
                        value={form.photoUrl}
                        onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
                        className="w-full pl-12 pr-4 py-4 rounded-xl bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                  </div>

                  {/* Image Preview */}
                  {form.photoUrl && (
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                        Photo Preview
                      </label>
                      <div className="relative group">
                        <img
                          src={form.photoUrl || "/placeholder.svg"}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-lg"
                          onError={(e) => {
                            e.target.style.display = "none"
                            e.target.nextSibling.style.display = "flex"
                          }}
                          onLoad={(e) => {
                            e.target.style.display = "block"
                            e.target.nextSibling.style.display = "none"
                          }}
                        />
                        <div className="w-full h-48 border-2 border-dashed border-red-300 dark:border-red-600 rounded-xl flex items-center justify-center bg-red-50 dark:bg-red-900/20 hidden">
                          <div className="text-center">
                            <FaExclamationTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                            <p className="text-sm text-red-500 dark:text-red-400 font-medium">Failed to load image</p>
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-center justify-center">
                          <FaEye className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-4 pt-4">
                    <button
                      type="submit"
                      disabled={loading || !selectedCommittee}
                      className={`flex-1 bg-gradient-to-r ${
                        selectedCommitteeData?.color || "from-blue-600 to-purple-600"
                      } hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none flex items-center justify-center space-x-3 shadow-lg`}
                    >
                      {loading ? (
                        <>
                          <FaSpinner className="w-5 h-5 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <FaSave className="w-5 h-5" />
                          <span>{editingMemberId ? "Update Member" : "Add Member"}</span>
                        </>
                      )}
                    </button>

                    {editingMemberId && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-6 py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 flex items-center space-x-2 font-medium"
                      >
                        <FaUndo className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Members List Section */}
          <div className="lg:col-span-3">
            <div
              data-animate
              id="members"
              className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-700 ${
                visibleSections.has("members") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
            >
              <div
                className={`h-2 bg-gradient-to-r ${selectedCommitteeData?.color || "from-purple-500 to-pink-500"}`}
              ></div>

              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-16 h-16 bg-gradient-to-r ${
                        selectedCommitteeData?.color || "from-purple-500 to-pink-500"
                      } rounded-2xl flex items-center justify-center shadow-lg`}
                    >
                      <FaUsers className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Committee Members</h2>
                      {selectedCommittee && (
                        <p className="text-gray-600 dark:text-gray-400 font-medium">{selectedCommittee}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {members.length > 0 && (
                      <>
                        <button
                          onClick={autoSortMembers}
                          disabled={isReordering}
                          className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 font-medium shadow-md hover:shadow-lg text-sm disabled:opacity-50"
                        >
                          <FaSortAmountUp className="w-4 h-4" />
                          <span>Auto Sort</span>
                        </button>
                        <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-full">
                          <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                            {members.length} member{members.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {!selectedCommittee ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaUsers className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Select a Committee</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">
                      Choose a committee from the dropdown to view and manage members
                    </p>
                  </div>
                ) : loading ? (
                  <div className="text-center py-20">
                    <FaSpinner className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-6" />
                    <p className="text-xl text-gray-600 dark:text-gray-400 font-medium">Loading members...</p>
                  </div>
                ) : members.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FaUsers className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Members Yet</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">
                      Add the first member to this committee using the form
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Drag and Drop Instructions */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
                      <div className="flex items-center space-x-3">
                        <FaGripVertical className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <p className="text-blue-800 dark:text-blue-200 font-medium text-sm">
                          Drag and drop members to reorder them, or use the arrow buttons. Changes are saved
                          automatically.
                        </p>
                      </div>
                    </div>

                    {members.map((member, index) => (
                      <div
                        key={member.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`group bg-gray-50 dark:bg-gray-700 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border-2 cursor-move ${
                          dragOverItem === index
                            ? "border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-200 dark:border-gray-600"
                        } ${draggedItem === index ? "opacity-50 scale-95" : ""}`}
                      >
                        <div className="p-6">
                          <div className="flex items-center space-x-4">
                            {/* Drag Handle */}
                            <div className="flex flex-col items-center space-y-2">
                              <FaGripVertical className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200" />
                              <div className="flex flex-col space-y-1">
                                <button
                                  onClick={() => moveMember(index, "up")}
                                  disabled={index === 0 || isReordering}
                                  className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                                  title="Move up"
                                >
                                  <FaArrowUp className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => moveMember(index, "down")}
                                  disabled={index === members.length - 1 || isReordering}
                                  className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                                  title="Move down"
                                >
                                  <FaArrowDown className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            {/* Member Photo */}
                            <div className="relative">
                              <img
                                src={member.photoUrl || "/placeholder.svg"}
                                alt={member.name}
                                className="w-16 h-16 object-cover rounded-full border-4 border-white dark:border-gray-600 shadow-lg"
                                onError={(e) => {
                                  e.target.src = "/placeholder.svg"
                                }}
                              />
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white dark:border-gray-700"></div>
                            </div>

                            {/* Member Info */}
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                                {member.name}
                              </h3>
                              <div className="flex items-center space-x-3">
                                <span
                                  className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                                    member.designation === "Head"
                                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                                      : member.designation === "Co-Head"
                                        ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                                        : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                                  }`}
                                >
                                  {member.designation}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                  Position #{index + 1}
                                </span>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(member)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 font-medium shadow-md hover:shadow-lg text-sm"
                              >
                                <FaEdit className="w-3.5 h-3.5" />
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDelete(member.id)}
                                className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 font-medium shadow-md hover:shadow-lg text-sm"
                              >
                                <FaTrash className="w-3.5 h-3.5" />
                                <span>Delete</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  )
}

export default CommitteeUpdateForm
